import React, { useState, useEffect } from 'react';
import { trashAPI } from '../api';

/**
 * AdminTrashBoards — admin recovery view for soft-deleted boards.
 *
 * Boards moved to trash live for 30 days, after which the daily scheduler
 * permanently purges them (Mongo cascade + R2 cleanup). Admins can restore
 * any board within that window or purge immediately to free space.
 */
const AdminTrashBoards = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await trashAPI.listBoards();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load trash.');
    } finally {
      setLoading(false);
    }
  };

  const restore = async (id) => {
    setBusy((s) => ({ ...s, [id]: 'restore' }));
    setError(null);
    try {
      await trashAPI.restoreBoard(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Restore failed.');
    } finally {
      setBusy((s) => { const c = { ...s }; delete c[id]; return c; });
    }
  };

  const purge = async (id) => {
    setBusy((s) => ({ ...s, [id]: 'purge' }));
    setError(null);
    try {
      await trashAPI.purgeBoard(id);
      setConfirmPurge(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Purge failed.');
    } finally {
      setBusy((s) => { const c = { ...s }; delete c[id]; return c; });
    }
  };

  if (loading) return <div style={{ padding: 40 }}><div className="spinner" /></div>;
  if (!data) return <div style={{ padding: 40 }}>{error || 'No data.'}</div>;

  const rows = data.rows || [];

  return (
    <div data-testid="admin-trash-boards" style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1100 }}>
      <header>
        <p style={kicker}>Recently deleted</p>
        <h2 style={titleStyle}>Boards in trash.</h2>
        <p style={italicCopy}>
          Boards stay here for {data.retention_days || 30} days after deletion, then they're
          permanently removed (database rows + image / document files). You can restore any
          board to its owner's workspace at any time before that.
        </p>
      </header>

      {error && (
        <div data-testid="trash-error" style={errorStyle}>{error}</div>
      )}

      <div data-testid="trash-table" style={tableWrap}>
        <div style={tableHead}>
          <span>Board</span>
          <span>Workspace</span>
          <span>Deleted</span>
          <span style={{ textAlign: 'right' }}>Contents</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>

        {rows.length === 0 && (
          <p style={emptyState}>The trash is empty.</p>
        )}

        {rows.map((r) => {
          const isBusy = !!busy[r.id];
          return (
            <div
              key={r.id}
              data-testid={`trash-row-${r.id}`}
              style={tableRow}
            >
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontSize: 14 }}>
                  {r.name}
                  {r.is_demo && (
                    <span style={demoTag}>· demo</span>
                  )}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--kahla-text-light)' }}>
                  {r.client_name ? `For ${r.client_name}` : '—'}
                </p>
              </div>

              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13 }}>
                  {r.owner_workspace || `@${r.owner_username}`}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--kahla-text-light)' }}>
                  @{r.owner_username}
                </p>
              </div>

              <div>
                <p style={{ margin: 0, fontSize: 13 }}>
                  {formatDate(r.deleted_at)}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: r.days_remaining <= 3 ? '#b35a4a' : 'var(--kahla-text-light)' }}>
                  {r.days_remaining != null
                    ? `Auto-purge in ${r.days_remaining} day${r.days_remaining === 1 ? '' : 's'}`
                    : ''}
                </p>
              </div>

              <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--kahla-text-light)' }}>
                {r.image_count} images · {r.document_count} docs
                {r.inspiration_count > 0 ? ` · ${r.inspiration_count} insp` : ''}
              </div>

              <div style={{ textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  data-testid={`trash-restore-${r.id}`}
                  onClick={() => restore(r.id)}
                  disabled={isBusy}
                  style={primaryBtn(isBusy)}
                >
                  {busy[r.id] === 'restore' ? '…' : 'Restore'}
                </button>
                <button
                  type="button"
                  data-testid={`trash-purge-${r.id}`}
                  onClick={() => setConfirmPurge(r)}
                  disabled={isBusy}
                  style={dangerBtn(isBusy)}
                >
                  Purge
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {confirmPurge && (
        <div
          data-testid="confirm-purge-modal"
          style={modalBackdrop}
          onClick={() => setConfirmPurge(null)}
        >
          <div
            style={modalBody}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={kicker}>Confirm permanent purge</p>
            <h3 style={{ ...titleStyle, fontSize: 22 }}>
              This cannot be undone.
            </h3>
            <p style={italicCopy}>
              "{confirmPurge.name}" and all its images, documents, and notes
              will be permanently removed from the database and from object storage.
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
              <button
                type="button"
                data-testid="cancel-purge-btn"
                onClick={() => setConfirmPurge(null)}
                style={ghostBtn(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="confirm-purge-btn"
                onClick={() => purge(confirmPurge.id)}
                disabled={!!busy[confirmPurge.id]}
                style={dangerBtn(!!busy[confirmPurge.id])}
              >
                {busy[confirmPurge.id] === 'purge' ? 'Purging…' : 'Purge permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return iso; }
}

const kicker = {
  fontFamily: 'Playfair Display, serif', fontSize: 11,
  letterSpacing: '0.32em', textTransform: 'uppercase',
  color: 'var(--kahla-text-light)', margin: '0 0 10px', fontWeight: 300,
};
const titleStyle = {
  fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 400,
  color: 'var(--kahla-charcoal)', margin: '0 0 8px', lineHeight: 1.2,
};
const italicCopy = {
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic',
  fontSize: 16, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.65, maxWidth: 720,
};
const tableWrap = { border: '1px solid var(--kahla-border)', background: '#fff' };
const tableHead = {
  display: 'grid',
  gridTemplateColumns: '1.6fr 1.2fr 1fr 0.9fr 1.1fr',
  alignItems: 'center', gap: 0,
  padding: '14px 18px', borderBottom: '1px solid var(--kahla-cream)',
  fontFamily: 'Playfair Display, serif',
  fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
};
const tableRow = {
  display: 'grid',
  gridTemplateColumns: '1.6fr 1.2fr 1fr 0.9fr 1.1fr',
  alignItems: 'center', gap: 0,
  padding: '14px 18px',
  borderBottom: '1px solid var(--kahla-cream)',
  fontSize: 13, color: 'var(--kahla-charcoal)',
};
const emptyState = {
  padding: 40, textAlign: 'center',
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic', color: 'var(--kahla-text-light)',
};
const errorStyle = {
  background: '#f8eee9', color: '#7a3a3a', padding: '10px 14px',
  fontSize: 13, fontStyle: 'italic',
};
const demoTag = {
  marginLeft: 8, fontSize: 10, letterSpacing: '0.18em',
  color: 'var(--kahla-text-light)',
};
const primaryBtn = (disabled) => ({
  background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
  padding: '7px 16px',
  fontFamily: 'inherit', fontSize: 10, letterSpacing: '0.18em',
  textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});
const dangerBtn = (disabled) => ({
  background: 'transparent', color: '#7a3a3a',
  border: '1px solid #c8a39a', padding: '6px 14px',
  fontFamily: 'inherit', fontSize: 10, letterSpacing: '0.18em',
  textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
});
const ghostBtn = (disabled) => ({
  background: 'transparent', color: 'var(--kahla-charcoal)',
  border: '1px solid var(--kahla-border)', padding: '6px 14px',
  fontFamily: 'inherit', fontSize: 10, letterSpacing: '0.18em',
  textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
});
const modalBackdrop = {
  position: 'fixed', inset: 0,
  background: 'rgba(20, 18, 14, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, padding: 20,
};
const modalBody = {
  background: '#fff', padding: '36px 40px',
  maxWidth: 460, width: '100%',
  border: '1px solid var(--kahla-border)',
};

export default AdminTrashBoards;
